import { api } from './api';

/** The header a password-confirmed endpoint reads its proof from. Matches `Reauthentication.Header`. */
export const REAUTH_HEADER = 'X-Reauthentication';

/** What a successful confirmation issues — a one-time proof, spent by the next protected call. */
export interface ReauthenticationIssued {
  token: string;
  expiresAt: string;
}

/**
 * Re-authentication: confirms the signed-in user's own password without changing anything.
 *
 * Lives in `lib/` rather than in a feature's api module because the caller is `<ConfirmDialog>`
 * — a shared component. A shared component must not import from `features/`, and this is the
 * only thing it needed from there.
 *
 * Resolves on success and throws on a bad password, so a caller can simply await it before
 * proceeding. The password is sent, checked, and dropped: it is never stored, never cached,
 * and never travels with the protected request itself.
 *
 * **It resolves to a one-time PROOF.** An endpoint that must be password-confirmed whoever calls
 * it — an evidence movement, an evidence delete — requires that proof in the
 * {@link REAUTH_HEADER} header and spends it; every other caller ignores it, as it ignored the
 * empty response before. It lives two minutes and works once.
 */
export function verifyPassword(password: string): Promise<ReauthenticationIssued> {
  return api<ReauthenticationIssued>('/api/auth/verify-password', {
    method: 'POST',
    body: { password },
    /*
      A 401 here means the password is wrong, not that the access token expired, so a refresh
      attempt would loop — the same reason the sign-in and change-password calls skip it.
    */
    skipRefresh: true,
  });
}

/** The header object a protected call passes through `api(..., { headers })`. */
export function reauthHeaders(token: string): Record<string, string> {
  return { [REAUTH_HEADER]: token };
}
